<!-- Title. The root screen: the street breathing at dusk on a live canvas,
     the tagline, one huge Play, and the four rows beneath it. -->
<template>
  <IonPage>
    <AppHeader title="Switch On Christmas" />

    <IonContent :fullscreen="true">
      <AppLargeTitle title="Switch On Christmas" />

      <div class="soc-hero title-hero" aria-hidden="true">
        <canvas ref="heroEl" />
      </div>

      <div class="soc-stack title-stack">
        <p class="title-tagline">{{ APP_TAGLINE }}</p>

        <IonButton class="soc-play" @click="play">Play</IonButton>

        <p class="title-total tnum" aria-live="polite">
          <template v-if="national"><strong>{{ formatTotal(national.total) }}</strong> lights across Australia</template>
          <template v-else>Every round you finish adds a light to Australia</template>
        </p>

        <IonList :inset="true" class="title-menu">
          <IonItem button :detail="true" @click="go('calendar')">
            <IonIcon slot="start" :icon="calendarOutline" aria-hidden="true" />
            <IonLabel>
              <h3>Today's Street</h3>
              <p>{{ todayLine }}</p>
            </IonLabel>
            <IonBadge v-if="today" slot="end">Day {{ today }}</IonBadge>
          </IonItem>
          <IonItem button :detail="true" @click="go('my-street')">
            <IonIcon slot="start" :icon="homeOutline" aria-hidden="true" />
            <IonLabel>
              <h3>My Street</h3>
              <p>{{ progress.unlockedDecorations.length }} of {{ DECORATIONS.length }} decorations unlocked</p>
            </IonLabel>
          </IonItem>
          <IonItem button :detail="true" @click="go('australia')">
            <IonIcon slot="start" :icon="sparklesOutline" aria-hidden="true" />
            <IonLabel>
              <h3>Australia lights up</h3>
              <p class="tnum">{{ national ? `${formatTotal(national.total)} lights so far` : 'The shared national display' }}</p>
            </IonLabel>
          </IonItem>
          <IonItem button :detail="true" @click="go('how-to-play')">
            <IonIcon slot="start" :icon="helpCircleOutline" aria-hidden="true" />
            <IonLabel><h3>How to play</h3></IonLabel>
          </IonItem>
          <IonItem button :detail="true" :lines="'none'" @click="go('settings')">
            <IonIcon slot="start" :icon="settingsOutline" aria-hidden="true" />
            <IonLabel><h3>Settings</h3></IonLabel>
          </IonItem>
        </IonList>

        <p v-if="progress.bestFreePlay > 0" class="soc-muted ion-text-center tnum">
          Personal best {{ formatTotal(progress.bestFreePlay) }}
        </p>
      </div>

      <p class="soc-foot">Brought to you by Origin</p>
    </IonContent>
  </IonPage>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import {
  IonPage, IonContent, IonButton, IonList, IonItem, IonLabel, IonIcon, IonBadge,
  onIonViewWillEnter, onIonViewDidLeave,
} from '@ionic/vue';
import { calendarOutline, homeOutline, sparklesOutline, helpCircleOutline, settingsOutline } from 'ionicons/icons';
import { useRouter } from 'vue-router';
import { APP_TAGLINE } from '../../app-config.js';
import { AppHeader, AppLargeTitle } from '../core/ui';
import { progress, national, settings, setNational, formatTotal, reducedMotion } from '../store.js';
import { fetchTotal } from '../lib/national.js';
import { dayIndexFor, DECORATIONS, CAMPAIGN_YEAR } from '../lib/daily.js';
import { generateLevel } from '../lib/level-gen.js';
import { createRenderer } from '../lib/renderer.js';

const router = useRouter();
const heroEl = ref(null);

const today = computed(() => dayIndexFor(new Date()));
const todayLine = computed(() => {
  if (today.value) return `Door ${today.value} is open. Same street for everyone.`;
  const now = new Date();
  const dec1 = new Date(Date.UTC(CAMPAIGN_YEAR, 11, 1, -11));      // 1 Dec, Sydney morning
  const days = Math.ceil((dec1 - now) / 86400000);
  return days > 0 ? `Opens 1 December — ${days} ${days === 1 ? 'day' : 'days'} to go` : 'Opens 1 December';
});

const go = (name) => router.push({ name });
const play = () => {
  if (!progress.seenHowTo) router.push({ name: 'how-to-play', query: { next: 'play' } });
  else router.push({ name: 'play', params: { mode: 'free' } });
};

// --- the ambient hero ---------------------------------------------------
let renderer = null;
let raf = 0;
let ambientLevel = null;
let ro = null;

function loop(t) {
  if (renderer && ambientLevel) renderer.drawAmbient(ambientLevel, t);
  // Under reduced motion one frame a second is plenty: nothing moves.
  if (reducedMotion()) { raf = 0; setTimeout(() => { if (renderer) raf = requestAnimationFrame(loop); }, 1000); return; }
  raf = requestAnimationFrame(loop);
}

function startHero() {
  if (!heroEl.value) return;
  if (!renderer) {
    renderer = createRenderer(heroEl.value, { highContrast: settings.highContrast, reducedMotion: reducedMotion() });
    ambientLevel = generateLevel('switch-on-christmas-ambient', { tier: 1, mechanics: [] });
    ro = new ResizeObserver(() => renderer?.resize());
    ro.observe(heroEl.value);
  }
  renderer.setOptions({ highContrast: settings.highContrast, reducedMotion: reducedMotion() });
  renderer.resize();
  if (!raf) raf = requestAnimationFrame(loop);
}

function stopHero() {
  if (raf) cancelAnimationFrame(raf);
  raf = 0;
}

watch(() => [settings.highContrast, settings.reducedMotion], () => renderer?.setOptions({ highContrast: settings.highContrast, reducedMotion: reducedMotion() }));

onMounted(async () => {
  startHero();
  const entry = await fetchTotal();
  if (entry) setNational(entry);
});
onIonViewWillEnter(startHero);
onIonViewDidLeave(stopHero);
onBeforeUnmount(() => {
  stopHero();
  ro?.disconnect();
  renderer?.destroy();
  renderer = null;
});
</script>

<style scoped>
.title-hero {
  aspect-ratio: 13 / 10;
  max-height: 46vh;
  margin: 0 auto;
  max-width: 34rem;
}

.title-stack {
  padding-top: var(--sp-5);
}
</style>
